import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, watchEffect, type MaybeRefOrGetter } from 'vue';
import {
  commentsService,
  type CommentableEntityType,
  type CommentTarget,
  type CommentThread,
  type TipTapNode,
} from '@/api/comments.service';
import {
  CommentSocketError,
  subscribeCommentCount,
  subscribeCommentList,
} from '@/queries/comment-realtime';
import { updateItemOnQueriesData } from '@/utils/update-query-data';

export const COMMENTS_QUERY_KEY = 'comments';
export const COMMENT_COUNTS_QUERY_KEY = 'comment-counts';

const toTarget = (target: MaybeRefOrGetter<CommentTarget>): CommentTarget => toValue(target);

const isCount = (value: unknown): value is number => typeof value === 'number';
const isThreadList = (value: unknown): value is CommentThread[] => Array.isArray(value);

const shouldRetryCommentQuery = (failureCount: number, error: Error): boolean => {
  if (error instanceof CommentSocketError && error.terminal) {
    return false;
  }
  return failureCount < 3;
};

const waitForSnapshot = <T>(
  existing: unknown,
  isValue: (value: unknown) => value is T,
  subscribe: (next: (value: T) => void, onError: (error: Error) => void) => () => void,
  signal?: AbortSignal,
): Promise<T> => {
  if (isValue(existing)) {
    return Promise.resolve(existing);
  }
  return new Promise((resolve, reject) => {
    const unsub = subscribe(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error) => {
        cleanup();
        reject(error);
      },
    );
    const handleAbort = (): void => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const cleanup = (): void => {
      unsub();
      signal?.removeEventListener('abort', handleAbort);
    };
    signal?.addEventListener('abort', handleAbort, { once: true });
  });
};

export const useComments = (
  target: MaybeRefOrGetter<CommentTarget>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  const queryClient = useQueryClient();
  const entityType = computed(() => toTarget(target).entityType);
  const entityId = computed(() => toTarget(target).entityId);
  const isEnabled = computed(() => Boolean(entityId.value) && Boolean(toValue(enabled)));
  const queryKey = computed(() => [COMMENTS_QUERY_KEY, entityType.value, entityId.value]);

  watchEffect((onCleanup) => {
    if (!isEnabled.value) {
      return;
    }
    const type = entityType.value;
    const id = entityId.value;
    const key = queryKey.value;
    onCleanup(
      subscribeCommentList(type, id, (threads) => {
        queryClient.setQueryData(key, threads);
      }),
    );
  });

  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      waitForSnapshot(
        queryClient.getQueryData(queryKey.value),
        isThreadList,
        (next, onError) => subscribeCommentList(entityType.value, entityId.value, next, onError),
        signal,
      ),
    enabled: isEnabled,
    staleTime: Infinity,
    retry: shouldRetryCommentQuery,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/** Unresolved thread count for one entity. Live snapshots arrive over the comments websocket. */
export const useComment = (
  entityType: MaybeRefOrGetter<CommentableEntityType>,
  entityId: MaybeRefOrGetter<string | null | undefined>,
) => {
  const queryClient = useQueryClient();
  const type = computed(() => toValue(entityType));
  const id = computed(() => toValue(entityId));
  const queryKey = computed(() => [COMMENT_COUNTS_QUERY_KEY, type.value, id.value]);

  watchEffect((onCleanup) => {
    if (!id.value) {
      return;
    }
    const key = queryKey.value;
    onCleanup(
      subscribeCommentCount(type.value, id.value, (count) => {
        queryClient.setQueryData(key, count);
      }),
    );
  });

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      const entityTypeValue = type.value;
      const entityIdValue = id.value;
      if (!entityIdValue) {
        throw new Error('Missing entity id');
      }
      return waitForSnapshot(
        queryClient.getQueryData(queryKey.value),
        isCount,
        (next, onError) => subscribeCommentCount(entityTypeValue, entityIdValue, next, onError),
        signal,
      );
    },
    enabled: () => Boolean(id.value),
    staleTime: Infinity,
    retry: shouldRetryCommentQuery,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useCreateComment = (target: MaybeRefOrGetter<CommentTarget>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { body: TipTapNode; parentId?: string | null }) =>
      commentsService.create(toTarget(target), {
        body: payload.body,
        ...(payload.parentId ? { parentId: payload.parentId } : {}),
      }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
    },
  });
};

export const useUpdateComment = (target: MaybeRefOrGetter<CommentTarget>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: TipTapNode }) =>
      commentsService.update(toTarget(target), commentId, { body }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
    },
  });
};

export const useResolveComment = (target: MaybeRefOrGetter<CommentTarget>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, resolved }: { commentId: string; resolved: boolean }) =>
      resolved
        ? commentsService.resolve(toTarget(target), commentId)
        : commentsService.unresolve(toTarget(target), commentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [COMMENTS_QUERY_KEY] });
    },
    onSuccess: (result: CommentThread, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData([COMMENTS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteComment = (target: MaybeRefOrGetter<CommentTarget>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => commentsService.remove(toTarget(target), commentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [COMMENTS_QUERY_KEY] });
    },
    onSuccess: (result, commentId, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, commentId, onMutateResult, context);
      queryClient.setQueriesData<CommentThread[]>({ queryKey: [COMMENTS_QUERY_KEY] }, (threads) =>
        threads
          ?.filter((thread) => thread.id !== commentId)
          .map((thread) => ({
            ...thread,
            replies: thread.replies.filter((reply) => reply.id !== commentId),
          })),
      );
    },
  });
};

export const useToggleCommentReaction = (target: MaybeRefOrGetter<CommentTarget>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, emoji }: { commentId: string; emoji: string }) =>
      commentsService.toggleReaction(toTarget(target), commentId, emoji),
    onMutate: () => ({ showSuccessToast: false }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
    },
  });
};
